import { BuildingDesignData, BuildingFaces, BuildingMeshMap, DesignData, RoofDesignData, RoofFaceFromPanda, RoofMesh, RoofSquareInfo, RoofStatus } from "../../../../../@types/types";
import { logger } from "../../../../../services/loggingService";
import { roofSquareService } from "../../../../../services/roofSquareService";
import { useViewerStore } from "../../../../../store/store";

export async function populateRoofSquareData (designListState: DesignData) {

    const {sceneViewer, isNonFinalizedSavedDesign, nonFinalizedDesignData,
        buildingWise33PercentRuleModuleCountList, accessoryBuildingList, roofSquareRetries, isRoofSquareInitialDataLoaded,
        roofSquareDataFromPanda, designList} = useViewerStore.getState();

    const account_id = sessionStorage.getItem('OppurtunityId');
    const sfid = sessionStorage.getItem('SalesForceId');
    const data_source = sessionStorage.getItem('dataSourceOrigin') ?? '';

    if (!sfid || !account_id) return;
    if (buildingWise33PercentRuleModuleCountList.length === 0) return;
    if (isNonFinalizedSavedDesign === null) return;

    let isSavedDesign = false, roofSquareData: RoofFaceFromPanda[], statusArray;

    let roofSquareDataFromPandaLocal = roofSquareDataFromPanda;
    if (!isRoofSquareInitialDataLoaded || roofSquareDataFromPanda.length === 0){
        const fetchedData = await roofSquareService.getRoofSquareData(account_id, data_source);
        roofSquareDataFromPandaLocal = fetchedData;
        useViewerStore.setState({ 
            roofSquareDataFromPanda: fetchedData, 
            isRoofSquareInitialDataLoaded: true });
    }

    const buildingFaces = getBuildingFaces(roofSquareDataFromPandaLocal);
    const isFirstLoad = (!designListState && !isNonFinalizedSavedDesign);

    if ( isFirstLoad) {
        isSavedDesign = false;
        statusArray = await initAllRoofAsActive();
        roofSquareData = roofSquareDataFromPandaLocal;
        useViewerStore.setState({activeStatuses: statusArray});
    } else {
        isSavedDesign = true;
        if(designListState.Buildings === null) designListState = designList[0];
        const buildings = isNonFinalizedSavedDesign ? nonFinalizedDesignData.Buildings : designListState.Buildings;
        const designData = isNonFinalizedSavedDesign ? nonFinalizedDesignData : designListState;
        statusArray = getStatusFromDesign(buildings, isNonFinalizedSavedDesign, buildingFaces);
        roofSquareData = getRoofSquareData(designData, buildingFaces);
        const faceIdArray = roofSquareDataFromPandaLocal.map(e => Number(e.faceId));
        const isFaceIdRepeating = faceIdArray.some((e, i) => faceIdArray.indexOf(e) !== i);
        if (isFaceIdRepeating) {
            useViewerStore.setState({
                isRoofSquareInitialDataLoaded: true,
                isRoofSquareDataUnavailable: true,
                roofSquareErrorMessage: 'Data Error: Face ID is repeating in the Roof Square Data',
            });
            return;
        }
        useViewerStore.setState({activeStatuses: statusArray});
        if (!isNonFinalizedSavedDesign) {
            const roofSquareTotal = roofSquareData?.reduce((total, e) => total + Number(e.roofSquare), 0);
            if (!roofSquareData && roofSquareTotal === 0) {
                if (roofSquareRetries < 2){
                    useViewerStore.setState({ roofSquareRetries: roofSquareRetries + 1 });
                    setTimeout(() => {
                        void populateRoofSquareData(designListState);
                    }, 0);
                }
                return;
            }
        }
    }


    // Check for repeating face IDs in roofSquareData
    const faceIdArray = roofSquareData.map(e => Number(e.faceId));
    const isFaceIdRepeating = faceIdArray.some((e, i) => faceIdArray.indexOf(e) !== i);
    if (isFaceIdRepeating) {
        useViewerStore.setState({
            isRoofSquareInitialDataLoaded: true,
            isRoofSquareDataUnavailable: true,
            isRoofSquarePriceUpdated: true,
            roofSquareErrorMessage: 'Data Error: Face ID is repeating in the Roof Square Data',
        });
        return;
    }

    if (roofSquareData.length === 0) {
        if (roofSquareRetries < 2){
            useViewerStore.setState({
                isRoofSquareInitialDataLoaded: false,
                roofSquareRetries: roofSquareRetries + 1,
             });
            setTimeout(() => {
                void populateRoofSquareData(designListState);
            }, 0);
        } else {
            useViewerStore.setState({ 
                isRoofSquareInitialDataLoaded: true,
                isRoofSquareDataUnavailable: true,
                isRoofSquarePriceUpdated: true,
                roofSquareErrorMessage: 'Data Error: Roof Square Data Missing',
            }); 
        }
    } else {
        roofSquareData = roofSquareData.filter(e => {
            const roofMesh = sceneViewer.roofMeshArr.find((roofMesh: RoofMesh) => Number(roofMesh.userData.roof_tag_id) === Number(e.faceId));
            if (roofMesh && !roofMesh.flat && !roofMesh.flatRoof && !accessoryBuildingList.includes(roofMesh.userData.buildingName)) return e;
        });
    
        const buildingWiseRoofMeshArr = mapBuildingsToMeshes(roofSquareData, sceneViewer.roofMeshArr);

        // Prepare data for roof square table
        const roofSquareTableData = Object.entries(buildingWiseRoofMeshArr).map(([buildingName, roofMeshArr]) => ({
            buildingName,
            data: getRoofSquareForRoofMeshArray(roofMeshArr, roofSquareData, statusArray, isSavedDesign),
        }));
        
        useViewerStore.setState({ 
            roofSquareTableData,
            isRoofSquareInitialDataLoaded: true,
            isRoofSquareDataUnavailable: false,
            roofSquareErrorMessage: '',
        });
    }

}

const initAllRoofAsActive = async (): Promise<RoofStatus[]> => {
    try {

        const { sceneViewer, buildingWise33PercentRuleModuleCountList, accessoryBuildingList, deactivatedRoofIdArr } = useViewerStore.getState();
        const buildingNames = buildingWise33PercentRuleModuleCountList
            .filter((building) => !building.isFlatRoof)
            .map((building) => building.name);

        // Set roof as active by default for each building
        let roofStatusArray: RoofStatus[] = [];
        buildingNames.forEach((building) => {
            sceneViewer.roofMeshArr.forEach((mesh: RoofMesh) => {
                if (mesh.flatRoof) return;
                if (accessoryBuildingList.includes(mesh.userData.buildingName)) return;
                if (mesh.userData.buildingName === building) {
                    roofStatusArray.push({
                        buildingId: building,
                        roofId: mesh.userData.roof_tag_id,
                        isSelected: deactivatedRoofIdArr.find(r=> r === mesh.userData.roof_tag_id) ? false : true,
                    });
                }
            });
        })

        return roofStatusArray;
    } catch (error) {
        logger.error(error, 'fetchAllRoofActiveStatus failed');
        return [];
    }
};

const getStatusFromDesign = (buildings: BuildingDesignData[], isNonFinalized: boolean, buildingFaces: BuildingFaces) => {
    const { deactivatedRoofIdArr } = useViewerStore.getState();
    let roofStatusArray: RoofStatus[] = [];

    buildings.forEach((building: BuildingDesignData) => {
        const roofs = building.Roofs;
        roofs.forEach((roof: RoofDesignData) => {
            const faceId = Number(roof.Designation.replace(/\D/g, ''));
            const buildingId = isNonFinalized 
                ? Object.keys(buildingFaces).find(key => buildingFaces[key].includes(faceId))
                : Number(building.Building_Name.replace(/\D/g, ''));
            if (!buildingId) return;
            const isSelected = deactivatedRoofIdArr.find(r=> r === faceId) ? false : roof.Active_Ind
            roofStatusArray.push({
                buildingId: buildingId,
                roofId: faceId,
                isSelected: isSelected,
            })
        });
    });

    return roofStatusArray;
}

function getBuildingFaces(data?: RoofFaceFromPanda[]): BuildingFaces {
    if (!data) return {};
    return data.reduce<BuildingFaces>((acc, item) => {
        (acc[item.buildingId] ??= []).push(Number(item.faceId));
        return acc;
    }, {});
}

const getRoofSquareData = (designData: DesignData, buildingFaces: BuildingFaces): RoofFaceFromPanda[] => {
    let roofSquareData: RoofFaceFromPanda[] = []
    designData.Buildings.forEach((building: BuildingDesignData) => {

        const roofs = building.Roofs;
        roofs.forEach((roof) => {
            const faceId = Number(roof.Designation.replace(/\D/g, ''));
            //find the face id in buildingFaces and get the building id ( key)
            const buildingId = Object.keys(buildingFaces).find(key => buildingFaces[key].includes(faceId));
            if (!buildingId) return;

            const roofSquare = Number(roof.Low_Square) === 0 ? Number(roof.Normal_Square) : Number(roof.Low_Square);
            const slopeType = Number(roof.Low_Square) === 0 ? "Normal" : "Low";
            roofSquareData.push({
                buildingId: String(buildingId),
                faceId: String(faceId),
                roofSquare: String(roofSquare),
                slope: slopeType,
            })
        });
    })

    return roofSquareData;
}

const getRoofSquareForRoofMeshArray = (roofMeshArr: RoofMesh[], roofSquareDataFromService: RoofFaceFromPanda[], statusArray: RoofStatus[], isSavedDesign: boolean) => {
    const {
        deactivatedRoofIdArr,
        useJune2026SquareCalculation,
    } = useViewerStore.getState();
    const returnArray: RoofSquareInfo[] = [];

    roofMeshArr.forEach((mesh: RoofMesh) => {
        const roofId = mesh.userData.roof_tag_id;
        const lookUpRoof = roofSquareDataFromService.find(e => Number(e.faceId) === Number(roofId));
        const slopeType = lookUpRoof?.slope ?? 'Low', auroraRoofSquare = lookUpRoof?.roofSquare;

        const totalRoofSquare = useJune2026SquareCalculation
            ? Number(auroraRoofSquare)
            : Number(auroraRoofSquare) + (isSavedDesign ? 0 : Number(lookUpRoof?.wastage));

        const lowSlopeSquare = slopeType === 'Low' ? totalRoofSquare : 0;
        const normalSlopeSquare = slopeType === 'Normal' || slopeType === 'Steep' ? totalRoofSquare : 0;
        const isSelected = statusArray.find((e) => e.roofId === roofId)?.isSelected ?? true;
        const isActive = deactivatedRoofIdArr.find((r)=> r === roofId) ? false : (isSavedDesign && isSelected);
        const isRoofAccessory = deactivatedRoofIdArr.find((r)=> r === roofId) ? true : false;
        const buildingId = mesh.userData.buildingName;

        mesh.userData.roofSquare = {
            lowSquare: lowSlopeSquare,
            normalSquare: normalSlopeSquare,
            isRoofSquareActive: isActive,
        }

        returnArray.push({
            isActive,
            isRoofAccessory,
            buildingId,
            slopeType,
            roofId,
            lowSlopeSquare,
            normalSlopeSquare,
            totalRoofSquare: slopeType === 'Low' || slopeType === 'Normal' || slopeType === 'Steep' ? Math.ceil(Number(totalRoofSquare)) : 0,
            nameOverride: mesh.userData.nameOverride ?? roofId,
        });
    });

    return returnArray;
};

function mapBuildingsToMeshes(data: RoofFaceFromPanda[], roofMeshArr: RoofMesh[]): BuildingMeshMap {
    const result: BuildingMeshMap = {};

    // Step 1: Create an object with building names and arrays of face IDs
    data.forEach((item) => {
        const buildingName = item.buildingId;
        const faceId = parseInt(item.faceId);
        if (!result[buildingName]) result[buildingName] = [];
        result[buildingName].push(
            // directly map to RoofMesh here instead of pushing faceId
            roofMeshArr.find((mesh: RoofMesh) => Number(mesh.userData.nameOverride ?? mesh.userData.roof_tag_id) === Number(faceId))!
        );
    });

    // Step 2 (optional, if you push meshes directly above you don’t need this loop)
    for (let buildingName in result) {
        result[buildingName] = result[buildingName].filter(Boolean); // remove undefined
    }

    return result;
}
